/**
 * The public authorization engine.
 *
 * Every protected question in Campuzon goes through `authorize`. It answers
 * one thing:
 *
 *   CAN this identity + DO this action + ON this resource + WITHIN a scope
 *   they actually hold?
 *
 * Four rules govern it:
 *
 *  1. DEFAULT DENY. Anything not explicitly established is refused. Being
 *     signed in, owning another workspace, or knowing a resource id proves
 *     nothing.
 *  2. NOTHING SUPPLIED IS TRUSTED. A `ResourceRef` is a CLAIM about what is
 *     being reached, and every field on it is validated against memberships
 *     the store holds. It is never evidence.
 *  3. AUTHENTICATION IS NOT AUTHORIZATION. A valid session establishes who
 *     someone is and nothing about what they may do.
 *  4. REFUSALS EXPLAIN THEMSELVES without leaking. A denial says why in terms
 *     of the caller's own access, never in terms of another tenant's records.
 */
import { scopeCovers } from './governance'
import {
  GRANTING_STATUSES,
  type AuthorizationDecision,
  type AuthorizationDenial,
  type Membership,
  type OrgRole,
  type Organization,
  type PublicPermission,
  type ResolvedWorkspace,
  type ResourceRef,
  type WorkspaceDescriptor,
} from '../types/workspace'
import type { Scope } from '../types/governance'
import type { Identity } from '../types/auth'
import type { StaffMember } from '../types/governance'
import type { InternalStaffLink } from '../types/workspace'

/** Everything the engine may read. It reads nothing else — no globals. */
export interface AuthorizationStore {
  memberships: Membership[]
  organizations: Organization[]
  roles: OrgRole[]
  workspaces: WorkspaceDescriptor[]
  internalLinks: InternalStaffLink[]
  staff: StaffMember[]
}

const MESSAGES: Record<AuthorizationDenial, string> = {
  not_authenticated: 'You are not signed in.',
  account_not_active:
    'Your account is not in a state that allows this. Your access has not been removed.',
  no_membership: 'You do not participate in anything that carries this.',
  membership_not_active:
    'Your membership here is not active, so it grants nothing while that stands.',
  no_permission: 'Your role here does not include this action.',
  out_of_scope: 'This is outside the campus or organization your role covers.',
  not_resource_owner: 'This record belongs to someone else.',
  internal_only: 'This is Campuzon internal administration and is not part of any account role.',
}

function deny(reason: AuthorizationDenial): AuthorizationDecision {
  return { allowed: false, reason, viaMembership: null, message: MESSAGES[reason] }
}

function allow(membershipId: string): AuthorizationDecision {
  return { allowed: true, reason: null, viaMembership: membershipId, message: 'Allowed.' }
}

/* --------------------------------------------------------------- membership */

/**
 * Memberships that currently grant something. A suspended, revoked, invited,
 * pending or declined membership is returned by nothing here — it exists in
 * the store for history, and history is not access.
 */
export function activeMemberships(store: AuthorizationStore, identityId: string): Membership[] {
  return store.memberships.filter(
    (item) => item.identityId === identityId && GRANTING_STATUSES.includes(item.status),
  )
}

/** Every membership, granting or not, for the access-review surfaces. */
export function allMemberships(store: AuthorizationStore, identityId: string): Membership[] {
  return store.memberships.filter((item) => item.identityId === identityId)
}

/* --------------------------------------------------------------- workspaces */

/**
 * The workspaces an identity may enter, derived from memberships every time.
 *
 * There is no stored list of "workspaces this user has" that could drift out
 * of step with their memberships, and no client-supplied workspace id is
 * consulted — which is what makes selecting a workspace incapable of granting
 * one.
 */
export function resolveWorkspaces(
  store: AuthorizationStore,
  identity: Identity | null,
): ResolvedWorkspace[] {
  if (!identity) return []
  /* A suspended or deactivated account opens nothing, whatever it holds. */
  if (identity.status === 'suspended' || identity.status === 'deactivated') return []

  const resolved: ResolvedWorkspace[] = []

  for (const membership of activeMemberships(store, identity.id)) {
    const role = store.roles.find((item) => item.id === membership.orgRoleId)
    if (!role) continue

    const organization = membership.organizationId
      ? store.organizations.find((item) => item.id === membership.organizationId) ?? null
      : null

    /* A suspended organization's workspaces close for everyone in it. */
    if (organization && organization.status !== 'active') continue

    for (const kind of role.workspaces) {
      const descriptor = store.workspaces.find((item) => item.kind === kind)
      if (!descriptor) continue

      resolved.push({
        id: `${membership.id}:${kind}`,
        kind,
        label: descriptor.label,
        ownerLabel: organization ? organization.name : identity.displayName,
        path: descriptor.path,
        organizationId: membership.organizationId,
        membershipId: membership.id,
        roleId: role.id,
        roleName: role.name,
        scope: membership.scope,
        permissions: role.permissions,
      })
    }
  }

  return resolved
}

/**
 * Whether a workspace may be entered. Called by the route guard, and it asks
 * the resolver rather than trusting the path that got us here.
 */
export function canEnterWorkspace(
  store: AuthorizationStore,
  identity: Identity | null,
  kind: string,
): AuthorizationDecision {
  if (!identity) return deny('not_authenticated')
  if (identity.status === 'suspended' || identity.status === 'deactivated') {
    return deny('account_not_active')
  }
  const match = resolveWorkspaces(store, identity).find((item) => item.kind === kind)
  return match ? allow(match.membershipId) : deny('no_membership')
}

/* ------------------------------------------------------------- authorization */

/**
 * The single decision. Layers are evaluated in order, and a failure at any
 * layer ends it — there is no path where a later layer rescues an earlier
 * refusal.
 */
export function authorize(
  store: AuthorizationStore,
  identity: Identity | null,
  permission: PublicPermission,
  resource: ResourceRef = {},
): AuthorizationDecision {
  /* 1. Authentication. */
  if (!identity) return deny('not_authenticated')

  /* 2. Account standing. A valid session on a suspended account grants nothing. */
  if (identity.status !== 'active') return deny('account_not_active')

  /* 3. Personal records short-circuit ONLY for the owner. Asking for someone
        else's personal record is refused here rather than falling through to
        a role that might happen to carry a similar permission. */
  if (resource.identityId && resource.identityId !== identity.id) {
    return deny('not_resource_owner')
  }

  /* 4. Membership. */
  const held = activeMemberships(store, identity.id)
  if (held.length === 0) {
    /* Distinguish "you have none" from "yours is not active", so the person
       is told which of the two situations they are in. */
    return allMemberships(store, identity.id).length > 0
      ? deny('membership_not_active')
      : deny('no_membership')
  }

  let sawPermission = false
  let sawOrganization = false

  for (const membership of held) {
    const role = store.roles.find((item) => item.id === membership.orgRoleId)
    if (!role) continue

    /* 5. Permission. */
    if (!role.permissions.includes(permission)) continue
    sawPermission = true

    /* 6. Resource ownership — the tenant isolation check. A membership in
          organization A never reaches organization B's records, however
          confidently the request names them. */
    if (resource.organizationId) {
      if (membership.organizationId !== resource.organizationId) continue
      sawOrganization = true
    }

    /* 7. Scope. Narrower held scope never widens to cover a broader target. */
    const target: Scope = {
      level: resource.campusId
        ? 'campus'
        : resource.institutionId
          ? 'institution'
          : membership.scope.level,
      tenantId: membership.scope.tenantId,
      institutionId: resource.institutionId ?? membership.scope.institutionId,
      campusId: resource.campusId ?? membership.scope.campusId,
    }
    if (!scopeCovers(membership.scope, target)) continue

    return allow(membership.id)
  }

  /* The most informative honest refusal, without naming what exists elsewhere. */
  if (!sawPermission) return deny('no_permission')
  if (resource.organizationId && !sawOrganization) return deny('not_resource_owner')
  return deny('out_of_scope')
}

/* ------------------------------------------------- internal administration */

/**
 * Internal staff resolution.
 *
 * This is deliberately the ONLY function that can produce internal authority,
 * and it consults a provisioned link table. It never reads a role from the
 * identity, from a request, or from anything a person can edit about
 * themselves — so no public account can promote itself.
 */
export function internalStaffFor(
  store: AuthorizationStore,
  identity: Identity | null,
): StaffMember | null {
  if (!identity) return null
  if (identity.status !== 'active') return null
  const link = store.internalLinks.find((item) => item.identityId === identity.id)
  if (!link) return null
  const staff = store.staff.find((item) => item.userId === link.staffUserId) ?? null
  /* A staff record that is not active grants nothing, link or no link. */
  return staff && staff.status === 'active' ? staff : null
}

export function requireInternal(
  store: AuthorizationStore,
  identity: Identity | null,
): AuthorizationDecision {
  if (!identity) return deny('not_authenticated')
  const staff = internalStaffFor(store, identity)
  return staff
    ? { allowed: true, reason: null, viaMembership: null, message: 'Internal staff.' }
    : deny('internal_only')
}

/* -------------------------------------------------------------- convenience */

/** Every permission an identity currently holds anywhere, for display only. */
export function effectivePublicPermissions(
  store: AuthorizationStore,
  identity: Identity | null,
): PublicPermission[] {
  if (!identity) return []
  const set = new Set<PublicPermission>()
  for (const membership of activeMemberships(store, identity.id)) {
    const role = store.roles.find((item) => item.id === membership.orgRoleId)
    role?.permissions.forEach((key) => set.add(key))
  }
  return [...set].sort()
}

/**
 * Whether a role may be granted by a member. It refuses two things outright:
 * granting a role the granter does not hold the permissions for, and any
 * attempt to grant internal authority, which is not expressible here at all.
 */
export function canGrantRole(
  store: AuthorizationStore,
  identity: Identity | null,
  organizationId: string,
  targetRoleId: string,
): AuthorizationDecision {
  const base = authorize(store, identity, 'provider.members.manage', { organizationId })
  const communityFallback =
    !base.allowed && authorize(store, identity, 'community.members.manage', { organizationId })
  const decision = base.allowed ? base : communityFallback || base
  if (!decision.allowed) return decision

  const target = store.roles.find((item) => item.id === targetRoleId)
  if (!target) return deny('no_permission')

  /* No escalation: every permission the target role carries must already be
     held by the granter. */
  const mine = new Set(effectivePublicPermissions(store, identity))
  const escalates = target.permissions.some((key) => !mine.has(key))
  return escalates ? deny('no_permission') : decision
}
